// Payment Success page - Redirect landing after Stripe checkout
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/api/client';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying');
  const invoiceId = searchParams.get('invoice_id');

  useEffect(() => {
    const verifyPayment = async () => {
      // Wait 2 seconds for Stripe webhook to process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      try {
        // Refresh invoice data to confirm payment
        const billing = await api.getInvoices();
        const invoice = billing.invoices?.find(inv => inv.id === invoiceId);
        
        if (invoice?.status === 'paid') {
          setStatus('success');
        } else {
          setStatus('pending');
        }
      } catch (err) {
        console.error('Failed to verify payment:', err);
        setStatus('error');
      }

      // Redirect to account page after 3 seconds
      setTimeout(() => {
        navigate('/account');
      }, 3000);
    };

    verifyPayment();
  }, [invoiceId, navigate]);

  return (
    <div className="min-h-screen bg-navy flex flex-col items-center justify-center p-6">
      <img 
        src="/images/logo-white.png" 
        alt="Sunlight Grounds Care" 
        className="h-20 w-auto mb-8"
      />

      {status === 'verifying' && (
        <>
          <div className="w-12 h-12 border-3 border-gold border-t-transparent rounded-full animate-spin mb-6"></div>
          <h1 className="font-dm text-xl font-semibold text-white text-center">
            Verifying payment...
          </h1>
          <p className="text-white/60 text-sm font-dm mt-2 text-center">
            Please wait while we confirm your payment
          </p>
        </>
      )}

      {status === 'success' && (
        <>
          <div className="w-16 h-16 rounded-full bg-green flex items-center justify-center mb-6">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <h1 className="font-dm text-xl font-semibold text-white text-center">
            Payment successful!
          </h1>
          <p className="text-white/60 text-sm font-dm mt-2 text-center">
            Thank you for your payment. Redirecting...
          </p>
        </>
      )}

      {status === 'pending' && (
        <>
          <div className="w-16 h-16 rounded-full bg-gold flex items-center justify-center mb-6">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <h1 className="font-dm text-xl font-semibold text-white text-center">
            Payment processing
          </h1>
          <p className="text-white/60 text-sm font-dm mt-2 text-center">
            Your payment is being processed. Redirecting...
          </p>
        </>
      )}

      {status === 'error' && (
        <>
          <div className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center mb-6">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </div>
          <h1 className="font-dm text-xl font-semibold text-white text-center">
            Verification failed
          </h1>
          <p className="text-white/60 text-sm font-dm mt-2 text-center">
            Please check your account for payment status. Redirecting...
          </p>
        </>
      )}
    </div>
  );
}
